import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import cuideoTheme from "../../../../../../containers/themes/defaultTheme";
import { useUpdateContractEndNote } from "../../api/updateContractEndNote";

const StyledContractName = styled(Typography)`
  color: ${cuideoTheme.palette.primary.main};
  font-size: 0.875rem;
  margin: 0 0 1rem;
`;

// The theme fixes the inputs to 50px, which would crush the textarea rows.
const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    height: auto;
    align-items: flex-start;
  }
`;

const StyledErrorAlert = styled(Alert)`
  margin: 1rem 0 0;
`;

const StyledButtonProgress = styled(CircularProgress)`
  margin-left: 0.5rem;
`;

type TEditEndNoteDialogProps = {
  open: boolean;
  contractId: string;
  contractName?: string;
  currentValue?: string;
  onClose: () => void;
};

const EditEndNoteDialog = ({
  open,
  contractId,
  contractName,
  currentValue,
  onClose,
}: TEditEndNoteDialogProps) => {
  const [value, setValue] = useState(currentValue ?? "");
  const mutation = useUpdateContractEndNote();
  const { reset } = mutation;

  useEffect(() => {
    if (open) {
      setValue(currentValue ?? "");
      reset();
    }
  }, [open, currentValue, reset]);

  const handleSave = () => {
    mutation.mutate(
      { contractId, A_ade_nota_del_candidato_a: value },
      { onSuccess: onClose }
    );
  };

  // The backend stores the note trimmed, so only spaces around is no change.
  const isSaveDisabled =
    value.trim() === (currentValue ?? "").trim() || mutation.isLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-end-note-dialog-title"
    >
      <DialogTitle id="edit-end-note-dialog-title">
        <FormattedMessage
          id="EndNote.Editar nota de la candidata"
          defaultMessage="Editar nota de la candidata"
        />
      </DialogTitle>
      <DialogContent>
        {contractName && (
          <StyledContractName>{contractName}</StyledContractName>
        )}
        <StyledTextField
          multiline
          minRows={6}
          maxRows={16}
          fullWidth
          variant="outlined"
          size="small"
          id="end-note"
          name="endNote"
          label={<FormattedMessage id="EndNote.Nota" defaultMessage="Nota" />}
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={mutation.isLoading}
        />
        {mutation.isError && (
          <StyledErrorAlert severity="error">
            <FormattedMessage
              id="EndNote.No se ha podido guardar la nota"
              defaultMessage="No se ha podido guardar la nota"
            />
          </StyledErrorAlert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isLoading}>
          <FormattedMessage id="EndNote.Cancelar" defaultMessage="Cancelar" />
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          <FormattedMessage id="EndNote.Guardar" defaultMessage="Guardar" />
          {mutation.isLoading && (
            <StyledButtonProgress size={16} thickness={5} color="inherit" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEndNoteDialog;
